/**
 * Sincronización de lo hecho sin conexión (ola 2 · O2.5, propuestas 17.2/17.3/17.6).
 *
 * - Resultados de tests: POST /offline/sync en lotes de 20; el servidor corrige
 *   con la clave real y devuelve synced/skipped/rejected. Solo se borra de
 *   IndexedDB lo confirmado (synced ∪ skipped ∪ rechazos no reintentables).
 * - Repasos de flashcards: POST /flashcards/review uno a uno en orden de
 *   reviewed_at (el servidor aplica FSRS; aquí no se recalcula nada). 404/4xx →
 *   se descarta (la carta ya no está en el mazo); red/5xx/401 → se para y se
 *   conserva el resto.
 * - Mutex de módulo: dos llamadas concurrentes comparten la misma promesa.
 * - Nunca se borra nada si el POST falla: la idempotencia del servidor por
 *   session_local_id hace inocuo el reenvío.
 */
import { toast } from 'sonner';
import apiClient from './api';
import { API } from '../config/api';
import { offlineStorage } from './offlineStorage';

export const RESULT_BATCH_SIZE = 20;
// Rechazos que no cambian al reenviar: se descartan del móvil. `id_conflict`
// (el id ya existe en otra cuenta) se reintenta UNA vez con un id nuevo.
export const NON_RETRYABLE_REASONS = Object.freeze([
  'legacy_format', 'unknown_question', 'invalid_session_id', 'invalid_payload',
]);

let inFlight = null;

export const newSessionLocalId = () => `offline_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

export const isNetworkError = (error) => !error || !error.response;
export const isSessionError = (error) => [401, 403].includes(error?.response?.status);
export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Reintento exponencial (3 intentos) solo ante red o 5xx; un 4xx no se reintenta. */
export async function postWithRetry(fn, { tries = 3, baseDelay = 1000, wait = sleep } = {}) {
  let delay = baseDelay;
  let lastError = null;
  for (let attempt = 0; attempt < tries; attempt += 1) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      const status = error?.response?.status;
      if (status && status < 500) throw error;
      if (attempt < tries - 1) {
        await wait(delay);
        delay *= 2;
      }
    }
  }
  throw lastError;
}

export const emptySummary = () => ({
  tests: 0,
  reviews: 0,
  xp: 0,
  failed_added: 0,
  streak_days: null,
  discarded_tests: 0,
  discarded_reviews: 0,
  errors: 0,
  session_error: false,
  sessions: [],
});

const byCompletedAt = (a, b) => String(a?.completed_at || '').localeCompare(String(b?.completed_at || ''));

async function postSessions(batch) {
  const response = await postWithRetry(() => apiClient.post(`${API}/offline/sync`, { sessions: batch }));
  return response?.data || {};
}

/** Aplica la respuesta de un lote al móvil y al resumen; devuelve los ids en conflicto. */
async function applySyncResponse(data, summary) {
  const synced = Array.isArray(data.synced) ? data.synced : [];
  const skipped = Array.isArray(data.skipped) ? data.skipped : [];
  const rejected = Array.isArray(data.rejected) ? data.rejected : [];
  for (const session of synced) {
    await offlineStorage.saveSyncedResult(session);
    summary.sessions.push(session);
  }
  const discard = rejected
    .filter((r) => NON_RETRYABLE_REASONS.includes(r?.reason))
    .map((r) => r.session_local_id)
    .filter(Boolean);
  const conflicts = rejected
    .filter((r) => r?.reason === 'id_conflict')
    .map((r) => r.session_local_id)
    .filter(Boolean);
  const toClear = [...synced.map((s) => s.session_local_id), ...skipped, ...discard].filter(Boolean);
  if (toClear.length > 0) await offlineStorage.clearPendingResults(toClear);
  summary.tests += synced.length;
  summary.xp += Number(data.xp_earned) || 0;
  summary.failed_added += Number(data.failed_added) || 0;
  summary.discarded_tests += discard.length;
  if (data.streak_days !== undefined && data.streak_days !== null) summary.streak_days = data.streak_days;
  if (rejected.length > discard.length + conflicts.length) summary.errors += 1; // db_error: se reintenta más tarde
  return conflicts;
}

/** id_conflict: la sesión se reescribe con un id nuevo y se reenvía una sola vez. */
async function retryConflicts(conflicts, batch, summary) {
  const renamed = [];
  for (const oldId of conflicts) {
    const original = batch.find((s) => s?.session_local_id === oldId);
    if (!original) continue;
    const newId = newSessionLocalId();
    const copy = { ...original, session_local_id: newId, offline_session_id: newId, renamed_from: oldId };
    await offlineStorage.savePendingResult(copy);
    await offlineStorage.clearPendingResults([oldId]);
    renamed.push(copy);
  }
  if (renamed.length === 0) return;
  const again = await applySyncResponse(await postSessions(renamed), summary);
  if (again.length > 0) summary.errors += 1; // sigue en conflicto: se conserva para el próximo intento
}

/** Envía la cola de resultados. Muta y devuelve `summary`. */
export async function syncPendingResults(summary = emptySummary()) {
  const pending = await offlineStorage.getPendingResults();
  const ordered = [...(pending || [])].sort(byCompletedAt);
  for (let i = 0; i < ordered.length; i += RESULT_BATCH_SIZE) {
    const batch = ordered.slice(i, i + RESULT_BATCH_SIZE);
    try {
      const conflicts = await applySyncResponse(await postSessions(batch), summary);
      if (conflicts.length > 0) await retryConflicts(conflicts, batch, summary);
    } catch (error) {
      summary.errors += 1;
      if (error?.response?.status === 404) summary.unavailable = true; // ruta ausente: backend anterior
      if (isSessionError(error)) summary.session_error = true;
      break; // se conserva el lote entero; se reintentará al reconectar
    }
  }
  return summary;
}

/**
 * Envío directo de sesiones que NO pudieron guardarse en IndexedDB (cuota,
 * pestaña bloqueada): viven solo en memoria hasta que el servidor las corrige.
 * Devuelve la respuesta del servidor (o lanza).
 */
export async function syncSessionsDirect(sessions) {
  const data = await postSessions(Array.isArray(sessions) ? sessions : [sessions]);
  for (const session of Array.isArray(data.synced) ? data.synced : []) {
    try {
      await offlineStorage.saveSyncedResult(session);
    } catch (_e) {
      // el almacenamiento sigue fallando: el resultado se muestra desde la respuesta
    }
  }
  return data;
}

/** Un repaso que ya respondió 200 se retira; si la limpieza falla, queda marcado `sent`
 * para no reenviarlo. Desde el cableado B1 el servidor deduplica por `review_id`
 * (POST /flashcards/review es idempotente), así que un reenvío no repetiría FSRS ni XP:
 * la marca solo ahorra la petición. */
async function retireReview(review) {
  try {
    await offlineStorage.clearPendingReviews([review.review_id]);
  } catch (_e) {
    try {
      await offlineStorage.savePendingReview({ ...review, sent: true, sent_at: new Date().toISOString() });
    } catch (_e2) {
      // sin almacenamiento: el mutex evita el doble envío dentro de esta sesión
    }
  }
}

/** Envía la cola de repasos, uno a uno y en orden. Muta y devuelve `summary`. */
export async function syncPendingReviews(summary = emptySummary()) {
  const pending = await offlineStorage.getPendingReviews();
  for (const review of pending || []) {
    if (!review?.card_id || !review?.review_id) continue;
    if (review.sent) {
      await retireReview(review); // ya contó: solo falta limpiarlo
      continue;
    }
    try {
      const response = await apiClient.post(`${API}/flashcards/review`, {
        card_id: review.card_id,
        rating: review.rating,
        origen: 'offline',
        session_id: review.session_id || null,
        review_id: review.review_id, // idempotente en el servidor desde el cableado B1 (routers/flashcards.py)
      });
      summary.reviews += 1;
      summary.xp += Number(response?.data?.xp_earned) || 0;
      const streak = response?.data?.streak_days;
      if (streak !== undefined && streak !== null) summary.streak_days = streak;
      await retireReview(review);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 404 || status === 400 || status === 422) {
        await offlineStorage.clearPendingReviews([review.review_id]);
        summary.discarded_reviews += 1;
        continue;
      }
      summary.errors += 1;
      if (isSessionError(error)) summary.session_error = true;
      break; // red, 5xx o sesión caducada: parar y conservar el resto
    }
  }
  return summary;
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** Texto único del aviso de sincronización (vacío si no se envió nada). */
export const summaryText = (s) => {
  if (!s) return '';
  const parts = [];
  if (s.tests > 0) parts.push(plural(s.tests, 'test', 'tests'));
  if (s.reviews > 0) parts.push(plural(s.reviews, 'repaso', 'repasos'));
  if (parts.length === 0) return '';
  if (s.xp > 0) parts.push(`+${s.xp} XP`);
  if (s.failed_added > 0) parts.push(`${plural(s.failed_added, 'fallo nuevo', 'fallos nuevos')} en Practicar falladas`);
  if (s.streak_days) parts.push(`racha de ${plural(s.streak_days, 'día', 'días')}`);
  return `Sincronizado: ${parts.join(' · ')}`;
};

/**
 * Sincroniza todo lo pendiente. `silent`: solo avisa si se envió algo (nunca
 * recordatorios controladores). Devuelve el resumen, o null si no procede.
 */
export async function syncPendingOffline({ silent = true, notify = toast } = {}) {
  if (inFlight) return inFlight;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return null;
  let token = null;
  try {
    token = localStorage.getItem('session_token');
  } catch (_e) {
    token = null;
  }
  if (!token) return null;

  inFlight = (async () => {
    const summary = emptySummary();
    try {
      await syncPendingResults(summary);
    } catch (_e) {
      summary.errors += 1;
    }
    if (!summary.session_error) {
      try {
        await syncPendingReviews(summary);
      } catch (_e) {
        summary.errors += 1;
      }
    }
    const text = summaryText(summary);
    if (text && (!silent || summary.tests + summary.reviews > 0)) notify.success(text);
    if (!silent && summary.discarded_tests > 0) {
      notify.warning(`Se descartaron ${plural(summary.discarded_tests, 'test antiguo', 'tests antiguos')} con formato incompatible`);
    }
    if (!silent && summary.discarded_reviews > 0) {
      notify.info(`${plural(summary.discarded_reviews, 'repaso descartado', 'repasos descartados')}: esas tarjetas ya no están en tu mazo`);
    }
    if (!silent && summary.unavailable) {
      notify.warning('La sincronización no está disponible en esta versión; tus tests siguen guardados en este dispositivo.');
    } else if (!silent && summary.errors > 0 && !summary.session_error) {
      notify.warning('Quedan resultados por enviar; se reintentará al reconectar');
    }
    if (!silent && !text && summary.errors === 0 && summary.discarded_tests + summary.discarded_reviews === 0) {
      notify.info('No hay nada pendiente de sincronizar');
    }
    return summary;
  })().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

export default syncPendingOffline;
